import React from 'react';
import { FieldVerificationCard } from './FieldVerificationCard';
import { 
  getConfidenceColor, 
  getConfidenceLabel, 
  formatFieldName,
  type VerificationQueueRow, 
  type ProgramVerification 
} from '../../services/rpc/verification';

interface ProgramVerificationDetailProps {
  program: VerificationQueueRow;
  verifications: ProgramVerification[];
  isLoading: boolean;
  onBack: () => void;
  onUpdateVerification: (
    verificationId: string,
    userAction: 'approve' | 'edit' | 'reject' | 'flag_learning',
    correctedValue?: any,
    correctionNotes?: string
  ) => void;
  isUpdating: boolean;
}

export const ProgramVerificationDetail: React.FC<ProgramVerificationDetailProps> = ({
  program,
  verifications,
  isLoading,
  onBack,
  onUpdateVerification,
  isUpdating,
}) => {
  // Group verifications by category
  const groupedVerifications = verifications.reduce((acc, verification) => {
    if (!acc[verification.fieldCategory]) {
      acc[verification.fieldCategory] = [];
    }
    acc[verification.fieldCategory].push(verification);
    return acc;
  }, {} as Record<string, ProgramVerification[]>);

  const categories = Object.keys(groupedVerifications);
  const pendingCount = verifications.filter(v => v.verificationStatus === 'pending_review').length;
  const approvedCount = verifications.filter(v => v.verificationStatus === 'approved').length;

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#6b7280' 
      }}>
        Loading program verifications...
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={onBack}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#ffffff',
              color: '#374151',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            ← Back to Queue
          </button>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0' }}>
            {program.programName}
          </h2>
        </div>

        {/* Program Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Total Fields</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              {program.totalFields}
            </div>
          </div>
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
          }}>
            <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '4px' }}>Pending Review</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#92400e' }}>
              {pendingCount}
            </div>
          </div>
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#d1fae5',
            border: '1px solid #6ee7b7',
          }}>
            <div style={{ fontSize: '14px', color: '#065f46', marginBottom: '4px' }}>Approved</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#065f46' }}>
              {approvedCount}
            </div>
          </div>
          <div style={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#f0f9ff',
            border: '1px solid #7dd3fc',
          }}>
            <div style={{ fontSize: '14px', color: '#0c4a6e', marginBottom: '4px' }}>Avg Confidence</div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              color: getConfidenceColor(program.avgConfidence),
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {program.avgConfidence.toFixed(1)}%
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                ({getConfidenceLabel(program.avgConfidence)})
              </span>
            </div>
          </div>
        </div>

        {/* Batch Actions */}
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            Batch Actions
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                const highConfidenceFields = verifications.filter(
                  v => v.verificationStatus === 'pending_review' && v.confidenceScore >= 80
                );
                highConfidenceFields.forEach(v => onUpdateVerification(v.id, 'approve'));
              }}
              disabled={isUpdating}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #10b981',
                backgroundColor: '#10b981',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '500',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                opacity: isUpdating ? 0.6 : 1,
              }}
            >
              Approve High Confidence (≥80%)
            </button>
            <button
              onClick={() => {
                const mediumConfidenceFields = verifications.filter(
                  v => v.verificationStatus === 'pending_review' && v.confidenceScore >= 60 && v.confidenceScore < 80
                );
                mediumConfidenceFields.forEach(v => onUpdateVerification(v.id, 'approve'));
              }}
              disabled={isUpdating}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #f59e0b',
                backgroundColor: '#ffffff',
                color: '#f59e0b',
                fontSize: '13px',
                fontWeight: '500',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                opacity: isUpdating ? 0.6 : 1,
              }}
            >
              Approve Medium Confidence (60-79%)
            </button>
          </div>
        </div>
      </div>

      {/* Field Verifications by Category */}
      {categories.length === 0 ? (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
        }}>
          <h3 style={{ color: '#374151', marginBottom: '8px' }}>No field verifications found</h3>
          <p style={{ color: '#6b7280' }}>
            No extraction data available for this program.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {categories.map(category => (
            <div key={category}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '16px',
                textTransform: 'capitalize',
                borderBottom: '2px solid #e5e7eb',
                paddingBottom: '8px',
              }}>
                {formatFieldName(category)} ({groupedVerifications[category].length} fields)
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
                gap: '16px' 
              }}>
                {groupedVerifications[category].map(verification => (
                  <FieldVerificationCard
                    key={verification.id}
                    verification={verification}
                    onUpdate={onUpdateVerification}
                    isUpdating={isUpdating}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};